import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { usePageMeta } from '@/hooks/usePageMeta';
import SmartBackLink from '@/components/common/SmartBackLink';

export default function PrivacyPolicy() {
  usePageMeta({
    title: 'Privacy Policy',
    description: 'LANERUNNER privacy policy. Learn how we collect, use, and protect your personal information when using our freight load board platform.'
  });
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-4 py-4">
          <SmartBackLink />
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3">Privacy Policy</h1>
            <p className="text-muted-foreground">Last Updated: June 25, 2026</p>
          </div>

          {/* Content Card */}
          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="prose prose-neutral dark:prose-invert max-w-none">
                
                {/* Introduction */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">INTRODUCTION</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    LaneRunner, Inc. ("Company," "we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website lanerunner.com and our freight load board platform (collectively, the "Service").
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.
                  </p>
                </section>

                {/* 1. INFORMATION WE COLLECT */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">1. INFORMATION WE COLLECT</h2>
                  
                  <h3 className="text-xl font-semibold mb-3">1.1 Information You Provide to Us</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We collect information that you voluntarily provide when you:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Register for an account</li>
                    <li>Create a user profile</li>
                    <li>Post or search for loads</li>
                    <li>Communicate with other users</li>
                    <li>Contact customer support</li>
                    <li>Subscribe to our service</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-3">This information may include:</p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Name and business name</li>
                    <li>Email address</li>
                    <li>Phone number and mobile number</li>
                    <li>Business address</li>
                    <li>Company information (MC number, DOT number, EIN)</li>
                    <li>Insurance information</li>
                    <li>Payment information (credit card details, billing address)</li>
                    <li>Equipment information (truck types, trailer types)</li>
                    <li>Preferred lanes and routes</li>
                    <li>Profile photos and business logos</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">1.2 Information Collected Automatically</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    When you access the Service, we automatically collect certain information, including:
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>IP address</li>
                    <li>Browser type and version</li>
                    <li>Device type and operating system</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website addresses</li>
                    <li>Clickstream data</li>
                    <li>Cookies and similar tracking technologies</li>
                    <li>Location data (with your permission)</li>
                    <li>Date and time of access</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">1.3 SMS/Text Message Information</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">If you opt in to receive SMS notifications:</p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Mobile phone number</li>
                    <li>Carrier information</li>
                    <li>SMS opt-in preferences (transactional, promotional)</li>
                    <li>Message delivery status</li>
                    <li>Opt-out requests</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">1.4 Information from Third Parties</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">We may receive information about you from:</p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Insurance verification services</li>
                    <li>FMCSA database and other regulatory databases</li>
                    <li>Credit reporting agencies (for business verification)</li>
                    <li>Payment processors</li>
                    <li>Analytics providers</li>
                  </ul>
                </section>

                {/* 2. HOW WE USE YOUR INFORMATION */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">2. HOW WE USE YOUR INFORMATION</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We use the information we collect for the following purposes:
                  </p>

                  <h3 className="text-xl font-semibold mb-3">2.1 To Provide and Maintain the Service</h3>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Create and manage your account</li>
                    <li>Facilitate load postings and searches</li>
                    <li>Connect carriers with shippers and brokers</li>
                    <li>Process subscription payments</li>
                    <li>Provide customer support</li>
                    <li>Send service-related communications</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">2.2 To Improve and Optimize the Service</h3>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Analyze usage patterns and trends</li>
                    <li>Develop new features and functionality</li>
                    <li>Conduct research and analytics</li>
                    <li>Monitor and improve performance</li>
                    <li>Fix bugs and technical issues</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">2.3 To Communicate with You</h3>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Send transactional messages (account notifications, security alerts, 2FA codes)</li>
                    <li>Send promotional messages (if opted in) about loads, features, and updates</li>
                    <li>Respond to your inquiries and support requests</li>
                    <li>Send administrative information about your account or subscription</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">2.4 To Ensure Safety and Security</h3>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Verify your identity and credentials</li>
                    <li>Verify insurance and licensing information</li>
                    <li>Detect and prevent fraud</li>
                    <li>Enforce our Terms and Conditions</li>
                    <li>Protect against security threats</li>
                    <li>Comply with legal obligations</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">2.5 For Marketing and Business Development</h3>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Send marketing communications (with your consent)</li>
                    <li>Conduct surveys and gather feedback</li>
                    <li>Analyze market trends</li>
                    <li>Improve our business operations</li>
                  </ul>
                </section>

                {/* 3. HOW WE SHARE YOUR INFORMATION */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">3. HOW WE SHARE YOUR INFORMATION</h2>

                  <h3 className="text-xl font-semibold mb-3">3.1 With Other Users</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Your profile information, including company name, contact information, equipment details, and insurance information, may be visible to other users of the Service to facilitate connections for freight opportunities.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">3.2 With Service Providers</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We share information with third-party service providers who perform services on our behalf:
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>Payment processors (for subscription billing)</li>
                    <li>SMS service providers (for text message delivery)</li>
                    <li>Cloud hosting providers</li>
                    <li>Analytics services</li>
                    <li>Customer support tools</li>
                    <li>Email service providers</li>
                    <li>Insurance verification services</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">3.3 For Legal Reasons</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">We may disclose your information:</p>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li>To comply with applicable laws, regulations, or legal processes</li>
                    <li>To respond to lawful requests from public authorities</li>
                    <li>To enforce our Terms and Conditions</li>
                    <li>To protect our rights, property, or safety</li>
                    <li>To protect the rights, property, or safety of our users or others</li>
                    <li>In connection with fraud prevention or investigation</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">3.4 Business Transfers</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    If we are involved in a merger, acquisition, sale of assets, bankruptcy, or reorganization, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control of your personal information.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">3.5 With Your Consent</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    We may share your information for any other purpose with your explicit consent.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">3.6 Aggregated or De-identified Information</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We may share aggregated or de-identified information that cannot reasonably be used to identify you.
                  </p>
                </section>

                {/* 4. SMS/TEXT MESSAGING */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">4. SMS/TEXT MESSAGING</h2>

                  <h3 className="text-xl font-semibold mb-3">4.1 Consent</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    By providing your mobile phone number and opting in, you consent to receive text messages from LaneRunner. You understand that consent is not required as a condition of purchasing or using our Service.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.2 Types of Messages</h3>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li><strong>Transactional:</strong> Two-factor authentication codes, security alerts, account notifications (cannot be opted out)</li>
                    <li><strong>Promotional:</strong> Load alerts, rate updates, market information (optional, can opt out)</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">4.3 Message Frequency</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Message frequency varies. You may receive up to 15-20 messages per day depending on your account activity and preferences.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.4 Opt-Out</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    You can opt out of promotional messages at any time by:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Texting STOP to our messaging number</li>
                    <li>Updating your preferences in your account settings</li>
                    <li>Contacting support@lanerunner.com</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Transactional messages cannot be disabled as they are necessary for account security.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.5 Message Charges</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Standard message and data rates from your mobile carrier apply. We do not charge for SMS messages.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.6 SMS Service Provider</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    We use third-party SMS service providers to deliver messages. Your phone number and related information may be shared with these providers to facilitate message delivery.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.7 SMS Privacy Policy</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    LaneRunner may collect phone numbers when users, customers, brokers, carriers, shippers, or prospects request information, create an account, speak with our team, or consent to receive text messages.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    LaneRunner does not sell, rent, share, or disclose consumer personal information, including phone numbers, SMS consent, or opt-in data, to third parties or affiliates for marketing, promotional, lead generation, or advertising purposes.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    SMS messages may include signup links, onboarding help, account support, sales follow-up, promotional updates, and freight platform communication. Message frequency may vary. Message and data rates may apply. Users may reply STOP to opt out or HELP for help.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">4.8 SMS Consent and Non-Sharing</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    SMS opt-in and phone numbers collected for SMS communications are not shared with third parties or affiliates for marketing purposes. Consent to receive SMS messages is not a condition of purchase or ongoing use of our Service. You may opt out of SMS messages at any time by replying STOP to any message, adjusting your preferences in account settings, or contacting support@lanerunner.com.
                  </p>
                </section>

                {/* 5. COOKIES AND TRACKING TECHNOLOGIES */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">5. COOKIES AND TRACKING TECHNOLOGIES</h2>

                  <h3 className="text-xl font-semibold mb-3">5.1 Types of Cookies We Use</h3>
                  <ul className="list-disc pl-6 mb-6 text-muted-foreground space-y-1">
                    <li><strong>Essential cookies:</strong> Required for the Service to function</li>
                    <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
                    <li><strong>Analytics cookies:</strong> Help us understand how users interact with the Service</li>
                    <li><strong>Marketing cookies:</strong> Track your activity for advertising purposes</li>
                  </ul>

                  <h3 className="text-xl font-semibold mb-3">5.2 Managing Cookies</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You can control cookies through your browser settings. Note that disabling certain cookies may affect the functionality of the Service.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">5.3 Do Not Track</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Our Service does not currently respond to "Do Not Track" signals from browsers.
                  </p>
                </section>

                {/* 6. DATA RETENTION */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">6. DATA RETENTION</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We retain your information for as long as necessary to:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Provide the Service to you</li>
                    <li>Comply with legal obligations</li>
                    <li>Resolve disputes</li>
                    <li>Enforce our agreements</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    When you cancel your account, we will delete or anonymize your personal information within a reasonable timeframe, except where we are required to retain it by law or for legitimate business purposes.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-3">Specific retention periods:</p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li><strong>Account information:</strong> Retained while account is active, deleted within 90 days of account closure</li>
                    <li><strong>Transaction records:</strong> Retained for 7 years for tax and accounting purposes</li>
                    <li><strong>SMS opt-in records:</strong> Retained for compliance purposes for 4 years after opt-out</li>
                    <li><strong>Support communications:</strong> Retained for 2 years</li>
                  </ul>
                </section>

                {/* 7. DATA SECURITY */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">7. DATA SECURITY</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We implement reasonable technical and organizational security measures to protect your information, including:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Encryption of data in transit (SSL/TLS)</li>
                    <li>Encryption of sensitive data at rest</li>
                    <li>Regular security assessments</li>
                    <li>Access controls and authentication</li>
                    <li>Secure payment processing</li>
                    <li>Regular backups</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security of your information.
                  </p>
                </section>

                {/* 8. YOUR PRIVACY RIGHTS */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">8. YOUR PRIVACY RIGHTS</h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Depending on your location, you may have certain rights regarding your personal information:
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.1 Access and Portability</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have the right to request access to the personal information we hold about you and to receive a copy in a portable format.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.2 Correction</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have the right to request correction of inaccurate or incomplete personal information. You can update most information directly in your account settings.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.3 Deletion</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have the right to request deletion of your personal information, subject to certain exceptions (e.g., legal obligations, legitimate business purposes).
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.4 Opt-Out of Marketing</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have the right to opt out of receiving marketing communications at any time.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.5 Restriction of Processing</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You may have the right to request that we restrict processing of your personal information in certain circumstances.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">8.6 Objection</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You may have the right to object to our processing of your personal information in certain circumstances.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-4">
                    To exercise any of these rights, please contact us at support@lanerunner.com or (855) 918-3520.
                  </p>
                </section>

                {/* 9. CHILDREN'S PRIVACY */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">9. CHILDREN'S PRIVACY</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information.
                  </p>
                </section>

                {/* 10. INTERNATIONAL DATA TRANSFERS */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">10. INTERNATIONAL DATA TRANSFERS</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those of your country. By using the Service, you consent to such transfers.
                  </p>
                </section>

                {/* 11. CALIFORNIA PRIVACY RIGHTS */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">11. CALIFORNIA PRIVACY RIGHTS</h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
                  </p>

                  <h3 className="text-xl font-semibold mb-3">11.1 Right to Know</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have the right to request information about the categories and specific pieces of personal information we have collected, the sources, purposes, and third parties with whom we share it.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">11.2 Right to Delete</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have the right to request deletion of your personal information, subject to certain exceptions.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">11.3 Right to Opt-Out of Sale</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    We do not sell your personal information. If our practices change, we will update this policy and provide you with the ability to opt out.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">11.4 Right to Non-Discrimination</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have the right not to be discriminated against for exercising your CCPA rights.
                  </p>

                  <p className="text-muted-foreground leading-relaxed mb-4">
                    To exercise your California privacy rights, contact us at support@lanerunner.com or (855) 918-3520.
                  </p>
                </section>

                {/* 12. CHANGES TO THIS PRIVACY POLICY */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">12. CHANGES TO THIS PRIVACY POLICY</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    We may update this Privacy Policy from time to time. We will notify you of material changes by:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Posting the updated policy on the Service</li>
                    <li>Updating the "Last Updated" date</li>
                    <li>Sending you an email notification (for significant changes)</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Your continued use of the Service after changes become effective constitutes your acceptance of the revised Privacy Policy.
                  </p>
                </section>

                {/* 13. THIRD-PARTY LINKS */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">13. THIRD-PARTY LINKS</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies.
                  </p>
                </section>

                {/* 14. CONTACT US */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">14. CONTACT US</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
                  </p>
                  <div className="bg-muted/50 rounded-lg p-6 mb-4">
                    <p className="font-semibold mb-2">LaneRunner, Inc.</p>
                    <p className="text-muted-foreground mb-1">Email: support@lanerunner.com</p>
                    <p className="text-muted-foreground mb-3">Phone: (855) 918-3520</p>
                    <p className="text-sm text-muted-foreground">
                      For privacy-specific inquiries, you may email: privacy@lanerunner.com
                    </p>
                  </div>
                </section>

                {/* 15. DATA PROTECTION OFFICER */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">15. DATA PROTECTION OFFICER</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    If required by applicable law, we will appoint a Data Protection Officer. Contact information will be provided here when applicable.
                  </p>
                </section>

                {/* 16. ADDITIONAL INFORMATION */}
                <section className="mb-10">
                  <h2 className="text-2xl font-semibold mb-4">16. ADDITIONAL INFORMATION</h2>

                  <h3 className="text-xl font-semibold mb-3">16.1 Nevada Residents</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Nevada residents have the right to opt out of the sale of certain personal information. We do not sell personal information as defined under Nevada law. If you have questions, contact us at support@lanerunner.com.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">16.2 European Economic Area (EEA) Residents</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    If you are located in the EEA, our legal basis for processing your information includes:
                  </p>
                  <ul className="list-disc pl-6 mb-4 text-muted-foreground space-y-1">
                    <li>Performance of a contract with you</li>
                    <li>Compliance with legal obligations</li>
                    <li>Your consent (where applicable)</li>
                    <li>Our legitimate business interests</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    You have additional rights under the GDPR, including the right to lodge a complaint with a supervisory authority.
                  </p>

                  <h3 className="text-xl font-semibold mb-3">16.3 Sensitive Personal Information</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We do not intentionally collect sensitive personal information (e.g., racial or ethnic origin, political opinions, religious beliefs, health information) except where necessary for specific purposes with your explicit consent.
                  </p>
                </section>

              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <footer className="mt-12 pt-8 border-t text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-muted-foreground">
              <p>© 2025 LaneRunner, Inc. All rights reserved.</p>
              <div className="flex gap-4">
                <Link to="/terms" className="hover:text-foreground transition-colors">
                  Terms of Service
                </Link>
                <span>•</span>
                <a href="mailto:support@lanerunner.com" className="hover:text-foreground transition-colors">
                  Contact
                </a>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}
